import { addDays, differenceInCalendarDays, parseISO, startOfWeek } from "date-fns";
import { toISODate } from "./localDate";

/**
 * Pure geometry for the Gantt time axis: turning `yyyy-MM-dd` strings into
 * pixels and back.
 *
 * Kept out of the components (and free of any React import) so the maths can
 * be asserted headlessly — every failure mode here is a plausible-looking
 * wrong number rather than a crash, which is exactly the kind that ships.
 * Same reasoning as sectionGeometry.ts / sectionDrag.ts in the IFC viewer.
 *
 * ── Two rules the rest of the Gantt depends on ──
 *
 * 1. **Positions are always in DAYS.** Granularity only changes `pxPerDay` and
 *    which ticks are labelled. Nothing downstream — least of all the drag
 *    maths — branches on it, so there is one code path to get right.
 *
 * 2. **`parseISO`, never `new Date(iso)`.** A date-only string through the
 *    native constructor is parsed as UTC midnight, which is the previous day
 *    in any negative-offset zone; the bar then draws one column to the left.
 *    The repo has already paid for this once — see the note in panelDates.ts.
 */

export type GanttGranularity = "day" | "week" | "month";

/** Pixels per day at each granularity. Chosen so a day column stays legible at
 *  "day", a week reads as a block at "week", and a quarter fits at "month". */
export const PX_PER_DAY: Record<GanttGranularity, number> = {
  day: 32,
  week: 12,
  month: 4,
};

export interface GanttScale {
  /** First day on the axis, `yyyy-MM-dd`. */
  origin: string;
  /** Total days drawn, inclusive of both ends. */
  days: number;
  pxPerDay: number;
  granularity: GanttGranularity;
}

// Re-exported: the Gantt's callers import it from here.
export { toISODate };

/** Whole days from `a` to `b` (negative when b precedes a). */
export function daysBetween(a: string, b: string): number {
  return differenceInCalendarDays(parseISO(b), parseISO(a));
}

/** `iso` shifted by `n` days. */
export function shiftISO(iso: string, n: number): string {
  return toISODate(addDays(parseISO(iso), n));
}

/**
 * The visible date range.
 *
 * The union with a window around today is not padding for its own sake: it
 * guarantees the today marker is always on the axis, and that there is always
 * empty axis to drag a bar into. A building whose issues all sit in the past
 * would otherwise open on a chart with nowhere to schedule.
 *
 * The start is snapped back to a Monday so week and month ticks line up with
 * the columns rather than starting mid-week.
 */
export function computeDateRange(
  dates: string[],
  today: Date = new Date(),
  /** Never return a span shorter than this — see `daysToFill`. */
  minDays = 0
): { start: string; end: string } {
  const todayISO = toISODate(today);
  let min = shiftISO(todayISO, -7);
  let max = shiftISO(todayISO, 30);

  for (const d of dates) {
    if (d < min) min = d;
    if (d > max) max = d;
  }
  // A week either side so the first and last bars are not flush to the edge.
  min = shiftISO(min, -7);
  max = shiftISO(max, 7);

  const start = toISODate(startOfWeek(parseISO(min), { weekStartsOn: 1 }));
  // The axis is never narrower than the chart drawn on it. A range that stops
  // mid-screen leaves the grid hanging in white space, and the empty axis past
  // the last bar is exactly where unscheduled work is dragged out to.
  //
  // Only the END grows. The start is the Monday every tick is aligned to, and
  // moving it would slide the whole axis sideways as the window is resized.
  const end = daysBetween(start, max) + 1 < minDays ? shiftISO(start, minDays - 1) : max;

  return { start, end };
}

/**
 * Whole days of axis that FIT in `px` at this granularity — what to pass as
 * `computeDateRange`'s `minDays` so the chart fills its container.
 *
 * Rounds DOWN, and the rounding is the whole point. Rounding up overruns the
 * container by up to a column, which raises a horizontal scrollbar; that
 * scrollbar eats the height, which raises a vertical one, which eats the width,
 * which changes this answer — the two chased each other and the bars visibly
 * flickered. Filling can never overflow: a scrollbar now appears only when the
 * DATA is genuinely wider than the chart.
 *
 * The cost is a bare strip at the right-hand edge, under one column wide (11px
 * at week, 31px at day). The extra pixel of slack absorbs the sub-pixel error
 * in a fractional container width, which `clientWidth` reports rounded.
 */
export function daysToFill(px: number, granularity: GanttGranularity): number {
  return px <= 0 ? 0 : Math.max(0, Math.floor((px - 1) / PX_PER_DAY[granularity]));
}

/**
 * A range for a READ-ONLY chart (the snapshot comparison): `computeDateRange`'s
 * padded window when it fits, otherwise the tightest Monday-aligned window
 * that still holds every bar, so the chart does not scroll sideways for
 * nothing.
 *
 * The padding exists for the live chart — a today window and empty axis to
 * drag into. A comparison has no drag, so when that padding is the ONLY thing
 * pushing the axis past the container, it is dropped: first the week either
 * side, then today itself if the bars alone fit and today does not. Only when
 * the bars themselves are wider than the container is the padded range kept,
 * and the chart scrolls.
 *
 * The spare days are split either side of the bars, then the start snaps back
 * to a Monday (the snap is undone if it would push the last bar off the end).
 */
export function fitDateRange(
  dates: string[],
  today: Date = new Date(),
  /** Whole days that fit the container — see `daysToFill`. */
  minDays = 0
): { start: string; end: string } {
  const padded = computeDateRange(dates, today, minDays);
  if (minDays <= 0 || dates.length === 0) return padded;
  if (daysBetween(padded.start, padded.end) + 1 <= minDays) return padded;

  const sorted = [...dates].sort();
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const todayISO = toISODate(today);
  const candidates: [string, string][] = [
    [first < todayISO ? first : todayISO, last > todayISO ? last : todayISO],
    [first, last],
  ];
  for (const [lo, hi] of candidates) {
    const span = daysBetween(lo, hi) + 1;
    if (span > minDays) continue;
    const centred = toISODate(
      startOfWeek(parseISO(shiftISO(lo, -Math.floor((minDays - span) / 2))), { weekStartsOn: 1 })
    );
    const monday = toISODate(startOfWeek(parseISO(lo), { weekStartsOn: 1 }));
    for (const start of [centred, monday, lo]) {
      if (daysBetween(start, hi) + 1 <= minDays) {
        return { start, end: shiftISO(start, minDays - 1) };
      }
    }
  }
  return padded;
}

export function buildScale(
  range: { start: string; end: string },
  granularity: GanttGranularity
): GanttScale {
  return {
    origin: range.start,
    days: daysBetween(range.start, range.end) + 1,
    pxPerDay: PX_PER_DAY[granularity],
    granularity,
  };
}

/** Left edge, in px, of the column for `iso`. */
export function dateToX(scale: GanttScale, iso: string): number {
  return daysBetween(scale.origin, iso) * scale.pxPerDay;
}

/**
 * The date whose column contains `x`. Floors, so any pixel within a column
 * maps to that column's day — the inverse of `dateToX` for every x it produces.
 */
export function xToDate(scale: GanttScale, x: number): string {
  return shiftISO(scale.origin, Math.floor(x / scale.pxPerDay));
}

/** Total axis width in px. */
export function scaleWidth(scale: GanttScale): number {
  return scale.days * scale.pxPerDay;
}

/**
 * A bar's box. The span is INCLUSIVE of the end day — a task that starts and
 * ends on the same day is one day wide, not zero, which is what makes a
 * single-day bar visible at all.
 */
export function barRect(
  scale: GanttScale,
  start: string,
  end: string
): { left: number; width: number } {
  const left = dateToX(scale, start);
  const span = daysBetween(start, end) + 1;
  return { left, width: Math.max(1, span) * scale.pxPerDay };
}

/** Left edge of today's column, for the marker. */
export function todayX(scale: GanttScale, today: Date = new Date()): number {
  return dateToX(scale, toISODate(today));
}

export interface GanttTick {
  /** First day of the tick's span. */
  iso: string;
  x: number;
  width: number;
  label: string;
}

/**
 * Axis ticks for the current granularity: one per day, per Monday-started
 * week, or per month. Labels are produced by the caller's formatter so the
 * scale stays free of i18n and date-fns locale wiring.
 */
export function ticks(
  scale: GanttScale,
  format: (iso: string, granularity: GanttGranularity) => string
): GanttTick[] {
  const out: GanttTick[] = [];
  const end = shiftISO(scale.origin, scale.days - 1);

  if (scale.granularity === "day") {
    for (let i = 0; i < scale.days; i++) {
      const iso = shiftISO(scale.origin, i);
      out.push({ iso, x: i * scale.pxPerDay, width: scale.pxPerDay, label: format(iso, "day") });
    }
    return out;
  }

  let cursor = scale.origin;
  while (cursor <= end) {
    const next =
      scale.granularity === "week"
        ? shiftISO(cursor, 7)
        : toISODate(new Date(parseISO(cursor).getFullYear(), parseISO(cursor).getMonth() + 1, 1));
    // The last tick is clipped to the axis rather than overhanging it.
    const stop = next > end ? shiftISO(end, 1) : next;
    out.push({
      iso: cursor,
      x: dateToX(scale, cursor),
      width: daysBetween(cursor, stop) * scale.pxPerDay,
      label: format(cursor, scale.granularity),
    });
    cursor = next;
  }
  return out;
}
